import { create } from "zustand";
import { devtools } from "zustand/middleware";

const useOrderStore = create(
  devtools(
    (set, get) => ({
      // Order header state - matches orderDetails from SetOrderHeader.jsx
      orderHeader: {
        custom_ordno: "",
        ordno: "",
        ordate: new Date().toISOString().split("T")[0],
        ordertyp: "G",
        ordtyp: "G",
        ordcusid: "",
        ordvendid: "",
        ordtptid: "",
        orddespatch: "",
        orderedby: "",
        pldate: "",
        prntp: "",
        is_scheme: 0,
        agent_salesman_code: "",
        private_marka: "",
        ordremark1: "",
        ordremark2: "",
      },

      // Lot modal state
      isLotModalOpen: false,

      // Item detail modal state
      isItemDetailModalOpen: false,
      editingItemIndex: null,

      // Protected cases (billed/closed) - not editable in edit mode
      protectedCases: [],

      // Lot data state
      lotData: [],

      // Rate order items state
      rateOrderItems: [
        {
          case_id: 1,
          itemname: null,
          rate: "",
          UOM: "",
          packratio: "",
          quantity: "",
          itemdetails: "",
        }
      ],

      // Column order items state (for column order form)
      columnOrderItems: [
        {
          caseId: 1,
          item: null,
          quantities: [],
          itemSizes: []
        }
      ],

      // Select boxes state for dropdowns
      selectBoxes: {
        customer: null,
        vendor: null,
        transporter: null,
        agentSalesman: null,
        printType: null,
        pricelist: null,
      },

      // Pricelist filters state
      pricelistFilters: { supid: "" },

      // Action to set order header
      setOrderHeader: (headerData) =>
        set({ orderHeader: headerData }, false, "set-order-header"),

      // Action to update specific header fields
      updateOrderHeader: (updates) =>
        set(
          (state) => ({
            orderHeader: { ...state.orderHeader, ...updates },
          }),
          false,
          "update-order-header"
        ),

      // Reset order header
      resetOrderHeader: () =>
        set(
          {
            orderHeader: {
              custom_ordno: "",
              ordno: "",
              ordate: new Date().toISOString().split("T")[0],
              ordertyp: "G",
              ordtyp: "G",
              ordcusid: "",
              ordvendid: "",
              ordtptid: "",
              orddespatch: "",
              orderedby: "",
              pldate: "",
              prntp: "",
              is_scheme: 0,
              agent_salesman_code: "",
              private_marka: "",
              ordremark1: "",
              ordremark2: "",
            },
          },
          false,
          "reset-order-header"
        ),

      // Utility function to check if header is complete
      isHeaderComplete: () => {
        const { orderHeader } = get();
        return !!(orderHeader.ordcusid && orderHeader.ordvendid);
      },

      // Actions for select boxes
      updateSelectBox: (key, value) =>
        set(
          (state) => ({
            selectBoxes: { ...state.selectBoxes, [key]: value },
          }),
          false,
          `update-select-box-${key}`
        ),

      // Action to set all select boxes at once
      setSelectBoxes: (selectBoxes) =>
        set({ selectBoxes }, false, "set-select-boxes"),

      // Action to reset select boxes
      resetSelectBoxes: () =>
        set(
          {
            selectBoxes: {
              customer: null,
              vendor: null,
              transporter: null,
              agentSalesman: null,
              printType: null,
              pricelist: null,
            },
          },
          false,
          "reset-select-boxes"
        ),

      // Actions for pricelist filters
      setPricelistFilters: (filters) =>
        set({ pricelistFilters: filters }, false, "set-pricelist-filters"),

      // Actions for lot modal
      setLotModalOpen: (isOpen) =>
        set({ isLotModalOpen: isOpen }, false, "set-lot-modal-open"),

      // Actions for item detail modal
      setItemDetailModalOpen: (isOpen) =>
        set({ isItemDetailModalOpen: isOpen }, false, "set-item-detail-modal-open"),

      setEditingItemIndex: (index) =>
        set({ editingItemIndex: index }, false, "set-editing-item-index"),

      // Actions for protected cases
      setProtectedCases: (protectedCases) =>
        set({ protectedCases }, false, "set-protected-cases"),

      // Actions for lot data
      setLotData: (lotData) =>
        set({ lotData }, false, "set-lot-data"),

      // Actions for rate order items
      setRateOrderItems: (rateOrderItems) =>
        set({ rateOrderItems }, false, "set-rate-order-items"),

      // Function to populate rate order items from existing order data
      populateRateOrderItemsFromOrder: (orderDetails = []) => {
        if (!orderDetails || orderDetails.length === 0) {
          return;
        }

        const rateOrderItems = orderDetails.map((detail, index) => {
          const itemDetail = detail.agorder_itemdetail;
          const itemMast = itemDetail?.agitemmast;
          
          // Create item selection object compatible with AsyncItemSelect
          const itemSelection = itemMast ? {
            value: itemDetail.agitemdtlId, // Use the full agitemdtlId as the value
            label: itemDetail.concatenatedName || `${itemMast.itemCode || ''} ${itemMast.itemName || ''}`, // Use backend-generated name
            // Match the AsyncItemSelect API structure
            itemuom: itemMast.itemuom,
            itmbasicuom: itemMast.itmbasicuom,
            conval: itemMast.aguomconval || 1,
            packratio: itemDetail.packratio,
            itmsztyp: itemDetail.itmsztyp
          } : null;

          return {
            case_id: detail.caseno || 1,
            itemname: itemSelection,
            rate: detail.rate || "",
            UOM: itemMast?.itemuom || "",
            packratio: itemDetail?.packratio || "",
            quantity: detail.qty || "",
            itemdetails: detail.itemdetails || ""
          };
        });

        set({ rateOrderItems }, false, "populate-rate-order-items-from-order");
      },

      // Actions for column order items
      setColumnOrderItems: (columnOrderItems) =>
        set({ columnOrderItems }, false, "set-column-order-items"),

      // Function to populate column order items from existing order data
      populateColumnOrderItemsFromOrder: (orderDetails = []) => {
        if (!orderDetails || orderDetails.length === 0) {
          return;
        }

        // Step 1: Group order details by base item ID (first 16 characters)
        const groupedItems = orderDetails.reduce((groups, detail) => {
          if (!detail.agorder_itemdetail || !detail.agorder_itemdetail.agitemdtlId) {
            return groups;
          }

          const baseItemId = detail.agorder_itemdetail.agitemdtlId.substring(0, 16);
          const caseKey = `${baseItemId}_${detail.caseno}`; // Include case number in grouping
          
          if (!groups[caseKey]) {
            groups[caseKey] = [];
          }
          groups[caseKey].push(detail);
          return groups;
        }, {});

        // Step 2: Create column order items for each group
        const columnOrderItems = Object.entries(groupedItems).map(([caseKey, details]) => {
          const firstDetail = details[0];
          const itemDetail = firstDetail.agorder_itemdetail;
          const itemMast = itemDetail?.agitemmast;

          // Create item selection object compatible with ItemSelectSizeGrouped
          const itemSelection = itemMast ? {
            value: itemDetail.agitemdtlId.substring(0, 16), // First 16 chars as used by ItemSelectSizeGrouped
            label: itemDetail.concatenatedName || `${itemMast.itemCode || ''} ${itemMast.itemName || ''}`.trim(),
            // Match the ItemSelectSizeGrouped API structure
            itemuom: itemMast.itemuom,
            itmbasicuom: itemMast.itmbasicuom,
            conval: itemMast.aguomconval || 1
          } : null;

          // Step 3: Create quantities array from all size variants in this group
          const quantities = details
            .filter(detail => detail.qty > 0) // Only include non-zero quantities
            .map(detail => {
              const itemDetail = detail.agorder_itemdetail;
              return {
                sz: itemDetail.sz,
                szuom: itemDetail.szuom,
                itmsztyp: itemDetail.itmsztyp,
                packratio: itemDetail.packratio,
                agitemdtlId: itemDetail.agitemdtlId,
                qty: detail.qty,
                col: `${itemDetail.sz}_${itemDetail.szuom}` // Simple column mapping
              };
            });

          return {
            caseId: firstDetail.caseno,
            item: itemSelection,
            quantities: quantities,
          };
        });

        set({ columnOrderItems }, false, "populate-column-order-items-from-order");
      },
    }),
    {
      name: "order-store", // Name for devtools
    }
  )
);

export default useOrderStore;
